declare global {
  interface Window {
    Telegram: {
      WebApp: TelegramWebApp
    }
  }
}

export type ImpactOccurredType = "light" | "medium" | "heavy" | "rigid" | "soft"
export type NotificationOccurredType = "error" | "success" | "warning"

interface TelegramWebApp {
  initData: string
  initDataUnsafe: Record<string, any>

  version: string
  platform: string
  colorScheme: "light" | "dark"
  themeParams: Record<string, string>

  isExpanded: boolean
  viewportHeight: number
  viewportStableHeight: number

  isFullscreen?: boolean

  MainButton: {
    text: string
    color: string
    textColor: string
    isVisible: boolean
    isProgressVisible: boolean
    isActive: boolean
    show(): void
    hide(): void
    enable(): void
    disable(): void
    showProgress(leaveActive?: boolean): void
    hideProgress(): void
    setText(text: string): void
    onClick(cb: () => void): void
    offClick(cb: () => void): void
  }

  BackButton: {
    isVisible: boolean
    show(): void
    hide(): void
    onClick(cb: () => void): void
    offClick(cb: () => void): void
  }

  HapticFeedback?: {
    impactOccurred(strength: ImpactOccurredType): void
    notificationOccurred(type: NotificationOccurredType): void
    // selectionChanged(): void
  }

  // Main methods
  ready(): void
  expand(): void
  close(): void

  // Colors
  setHeaderColor(color: string): void
  setBackgroundColor(color: string): void

  // Fullscreen API
  requestFullscreen?(): void
  exitFullscreen?(): void

  enableClosingConfirmation(): void
  disableClosingConfirmation(): void

  // Events
  onEvent(event: TelegramEventType, callback: () => void): void
  offEvent(event: TelegramEventType, callback: () => void): void

  // Other methods
  disableVerticalSwipes?(): void
  enableVerticalSwipes?(): void
  lockOrientation?(): void
  unlockOrientation?(): void
  openTelegramLink(url?: string): void
  openInvoice(
    url: string,
    callback?: (status: "paid" | "cancelled" | "failed") => void
  ): void

  shareMessage(message: string): void
}

type TelegramEventType =
  | "themeChanged"
  | "viewportChanged"
  | "mainButtonClicked"
  | "backButtonClicked"
  | "fullscreenChanged"

export const initTelegramData = () => {
  if (["local"].includes(import.meta.env.VITE_NODE_ENV)) {
    const USER = import.meta.env.VITE_DEV_NAME || "Daniil_DEV"

    const USERS_DATA = {
      Evgeniy_DEV: {
        initData:
          "query_id=AAGEzmwLAAAAAITObAtsjvbf&user=%7B%22id%22%3A191680132%2C%22first_name%22%3A%22%D0%96%D0%B5%D0%BA%D0%B0%22%2C%22last_name%22%3A%22%22%2C%22username%22%3A%22jtokan%22%2C%22language_code%22%3A%22ru%22%2C%22allows_write_to_pm%22%3Atrue%2C%22photo_url%22%3A%22https%3A%5C%2F%5C%2Ft.me%5C%2Fi%5C%2Fuserpic%5C%2F320%5C%2FW2NEMxJjw-ykSJTYsHD2Ijy4NiZkkL-1V6GLRucggGA.svg%22%7D&auth_date=1731586164&hash=c92813db6ce900fc71315cd2b8ddcb444ab03de9164dfc47def2d6112c1af4b3",
        id: 191680132
      },
      Daniil_DEV: {
        id: "434570321",
        initData:
          "query_id=AAFRBOcZAAAAAFEE5xl8Xh_H&user=%7B%22id%22%3A434570321%2C%22first_name%22%3A%22Daniil%22%2C%22last_name%22%3A%22%22%2C%22username%22%3A%22daniilkhlyvniuk%22%2C%22language_code%22%3A%22en%22%2C%22is_premium%22%3Atrue%2C%22allows_write_to_pm%22%3Atrue%2C%22photo_url%22%3A%22https%3A%5C%2F%5C%2Ft.me%5C%2Fi%5C%2Fuserpic%5C%2F320%5C%2F3jBxkrNIREzdjaWDpEtNItoTBVzD5_xFAWzfuWquGvo.svg%22%7D&auth_date=1771585483&signature=j47d75Yu2xDV_hkr66tYNrg4fCX_3W5ox_44-9mHifjqNVzyDFbU58BZqgCDgGCf5i39oRTH2GmYE3djO92GBQ&hash=f22c94816ebe6224cba18d3f8a341fe6239c64c1134aa70f426674e3841d36b9",
        photo_url:
          "https://t.me/i/userpic/320/3jBxkrNIREzdjaWDpEtNItoTBVzD5_xFAWzfuWquGvo.svg",
        timeZone: "Europe/Kiev"
      },
      Daniil_STAGING: {
        id: "434570321",
        initData:
          "query_id=AAFRBOcZAAAAAFEE5xnOw2dl&user=%7B%22id%22%3A434570321%2C%22first_name%22%3A%22Daniil%22%2C%22last_name%22%3A%22%22%2C%22language_code%22%3A%22en%22%2C%22is_premium%22%3Atrue%2C%22allows_write_to_pm%22%3Atrue%2C%22photo_url%22%3A%22https%3A%5C%2F%5C%2Ft.me%5C%2Fi%5C%2Fuserpic%5C%2F320%5C%2F3jBxkrNIREzdjaWDpEtNItoTBVzD5_xFAWzfuWquGvo.svg%22%7D&auth_date=1756033998&signature=JPm6waAIeRBfmgocyxwTNNdzKiJMmljlYjNJLUbsZlkXm7sK_VYm_1IP3DC4aKNnXlrk1xTl124IgKFvhb8-AA&hash=2e4608accb7c23eced993062f1afe9011f8d4821cf30c859dfa2ff877f1c0fd2"
      },
      Yellow_DEV: {
        id: "787382190",
        initData:
          "query_id=AAGuf-4uAAAAAK5_7i6Gn6rm&user=%7B%22id%22%3A787382190%2C%22first_name%22%3A%22Yellow%22%2C%22last_name%22%3A%22%22%2C%22username%22%3A%22Yellow10041%22%2C%22language_code%22%3A%22en%22%2C%22allows_write_to_pm%22%3Atrue%2C%22photo_url%22%3A%22https%3A%5C%2F%5C%2Ft.me%5C%2Fi%5C%2Fuserpic%5C%2F320%5C%2FvZpe5YZpq9TZD89JxV3hSkTq6mrvVLtVZA6Yy-L2ecg.svg%22%7D&auth_date=1771585431&signature=FGGvC565kyiudd-t7TWkIrQA8cteR_2h2iqAWCae74NwB1MpxejEnvq4N_r1FVVtTjJayOus7Dd0AHmrJYy2Aw&hash=d843edcbe3feb8e77912a508a6c095ebc14700f48d52a0f52617c413b98932c0",
        photo_url:
          "https://t.me/i/userpic/320/vZpe5YZpq9TZD89JxV3hSkTq6mrvVLtVZA6Yy-L2ecg.svg",
        timeZone: "Europe/Kiev"
      },
      Yellow_STAGE: {
        initData:
          "query_id=AAGuf-4uAAAAAK5_7i5eOQ7H&user=%7B%22id%22%3A787382190%2C%22first_name%22%3A%22Yellow%22%2C%22last_name%22%3A%22%22%2C%22username%22%3A%22Yellow10041%22%2C%22language_code%22%3A%22en%22%2C%22allows_write_to_pm%22%3Atrue%2C%22photo_url%22%3A%22https%3A%5C%2F%5C%2Ft.me%5C%2Fi%5C%2Fuserpic%5C%2F320%5C%2FvZpe5YZpq9TZD89JxV3hSkTq6mrvVLtVZA6Yy-L2ecg.svg%22%7D&auth_date=1766422921&signature=1QT58IyLFS1eoCDLhKLt6-LKfHOM1IS5U0kUkRpuRlaKVuAs84nuuBdUGBbK8OqdvF2adOqtTE0SOvnCzhB2Bg&hash=b0cb20407c58e1f327b26090c7c912d5d2eae32f5da6c82054d6dfc9d67bc4db",
        id: "787382190",
        photo_url:
          "https://t.me/i/userpic/320/vZpe5YZpq9TZD89JxV3hSkTq6mrvVLtVZA6Yy-L2ecg.svg",
        timeZone: "Europe/Rome"
      },
      Nikita_DEV: {
        id: "8142295964",
        initData:
          "query_id=AAGck1FlAwAAAJyTUWU4FMm_&user=%7B%22id%22%3A8142295964%2C%22first_name%22%3A%22%D0%9D%D0%B8%D0%BA%D0%B8%D1%82%D0%B0%22%2C%22last_name%22%3A%22%22%2C%22username%22%3A%22nik_burm%22%2C%22language_code%22%3A%22uk%22%2C%22allows_write_to_pm%22%3Atrue%2C%22photo_url%22%3A%22https%3A%5C%2F%5C%2Ft.me%5C%2Fi%5C%2Fuserpic%5C%2F320%5C%2F8-_6ChUY-iesphcWSwQJWOSWNq4PYmaSvPHVP2TkKju4Fplbczc8u10hrvcyZz5v.svg%22%7D&auth_date=1757508713&signature=CnBTqGAKAfSB7uYDHn-sO3v0W5A9QL7GnIEPFdUQMti6FJ1iRZDt8GH2tG6XRqa0heYG1kfhpI9qoUpVe8VaCQ&hash=62191e1c2d4a52657e7052fc58d734129acde7d12fb36f17a79b4dbb9522f7df",
        photo_url:
          "https://t.me/i/userpic/320/8-_6ChUY-iesphcWSwQJWOSWNq4PYmaSvPHVP2TkKju4Fplbczc8u10hrvcyZz5v.svg",
        timeZone: "Europe/Kiev"
      }
    }
    window.Telegram.WebApp = {
      // @ts-ignore
      initData: USERS_DATA[USER].initData,
      version: "2.0",
      platform: "web",
      colorScheme: "light",
      themeParams: {},
      initDataUnsafe: {
        user: {
          // @ts-ignore
          id: USERS_DATA[USER].id,
          // @ts-ignore
          photo_url: USERS_DATA[USER].photo_url || ""
        }
      }
    } as any
  } else {
    const tg = window.Telegram.WebApp

    const platform = tg.platform

    console.log("platform", platform)

    document.documentElement.setAttribute("data-platform", platform)

    tg?.ready?.()
    tg?.expand?.()
    tg?.disableVerticalSwipes?.()
    tg?.requestFullscreen?.()
  }
}
